import { Stack } from '@fluentui/react';
import { Dropdown, Field, Option } from '@fluentui/react-components';
import type {
  IField,
  IMapOptions,
  IMetadata,
} from '../../services/metadataService';

interface IMapOptionsProps {
  disabled?: boolean;
  metadata?: IMetadata;
  setMetadata: (metadata: IMetadata) => void;
  setMetadataStr: (metadataStr: string) => void;
}

const MapOptions = (props: IMapOptionsProps) => {
  const { metadata, setMetadata, setMetadataStr, disabled = false } = props;

  // Filter fields by type for CoordinateField (Address or Coordinate)
  const coordinateFieldOptions = (metadata?.fields || []).filter(
    (field: IField) =>
      field.fieldType === 'Address' || field.fieldType === 'Coordinate'
  );

  // Filter fields by type for MarkerField (String, Number, Address, Lookup, Date, AutoComplete, UserSelector)
  const markerFieldOptions = (metadata?.fields || [])
    .filter(
      (field: IField) =>
        field.fieldType === 'String' ||
        field.fieldType === 'Number' ||
        field.fieldType === 'Address' ||
        field.fieldType === 'Lookup' ||
        field.fieldType === 'Date' ||
        field.fieldType === 'AutoComplete' ||
        field.fieldType === 'UserSelector'
    )
    .filter(
      (field) => !['id', 'code', 'name'].includes(field.name.toLowerCase())
    );
  const markerOptions = [
    { name: 'Id', label: 'Id' },
    { name: 'Code', label: 'Code' },
    { name: 'Name', label: 'Name' },
    ...markerFieldOptions,
  ];

  const handleCoordinateFieldChange = (value: string) => {
    const newMapOptions: IMapOptions = {
      ...metadata?.mapOptions,
      coordinateField: value || undefined,
    };
    const newMetadata: IMetadata = {
      ...metadata,
      lookups: metadata?.lookups ?? [],
      fields: metadata?.fields ?? [],
      validations: metadata?.validations ?? [],
      tabs: metadata?.tabs ?? [],
      interactions: metadata?.interactions ?? [],
      cardLayout: metadata?.cardLayout,
      mapOptions: newMapOptions,
    };
    setMetadata(newMetadata);
    setMetadataStr(JSON.stringify(newMetadata, null, 2));
  };

  const handleMarkerFieldChange = (value: string) => {
    const newMapOptions: IMapOptions = {
      ...metadata?.mapOptions,
      markerField: value || undefined,
    };
    const newMetadata: IMetadata = {
      ...metadata,
      lookups: metadata?.lookups ?? [],
      fields: metadata?.fields ?? [],
      validations: metadata?.validations ?? [],
      tabs: metadata?.tabs ?? [],
      interactions: metadata?.interactions ?? [],
      cardLayout: metadata?.cardLayout,
      mapOptions: newMapOptions,
    };
    setMetadata(newMetadata);
    setMetadataStr(JSON.stringify(newMetadata, null, 2));
  };

  return (
    <Stack tokens={{ childrenGap: 20, padding: 20 }}>
      <Field label="Coordinate Field" required={false}>
        <Dropdown
          placeholder="Select a field"
          value={
            coordinateFieldOptions.find(
              (f) => f.name === metadata?.mapOptions?.coordinateField
            )?.label || ''
          }
          selectedOptions={
            metadata?.mapOptions?.coordinateField
              ? [metadata.mapOptions.coordinateField]
              : []
          }
          onOptionSelect={(_event, data) => {
            handleCoordinateFieldChange(data.optionValue || '');
          }}
          disabled={disabled || coordinateFieldOptions.length === 0}
        >
          {coordinateFieldOptions.map((field) => (
            <Option key={field.name} value={field.name} text={field.label}>
              {field.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <Field label="Marker Field" required={false}>
        <Dropdown
          placeholder="Select a field"
          value={
            markerOptions.find(
              (f) => f.name === metadata?.mapOptions?.markerField
            )?.label || ''
          }
          selectedOptions={
            metadata?.mapOptions?.markerField
              ? [metadata.mapOptions.markerField]
              : []
          }
          onOptionSelect={(_event, data) => {
            handleMarkerFieldChange(data.optionValue || '');
          }}
          disabled={disabled}
        >
          {markerOptions.map((field) => (
            <Option key={field.name} value={field.name} text={field.label}>
              {field.label}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </Stack>
  );
};

export default MapOptions;
